import type { Maybe } from '@adornis/base/utilTypes.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { config } from '@ils/config/config.js';
import { EmailAttachments, EmailType, EmailVars } from '@ils/mails/emailEnums.js';
import type { ExamDayRatingType, ExamType } from '@ils/static/enums.js';
import { ILSFile } from '@legacy-db/ils-files.js';
import { html } from 'lit';
import {
  getOrderConfirmAdminData,
  getOrderConfirmationMailData,
  getOrderRefundBaseData,
  getOrderRefundedNotificationMailData,
} from '../../server/mailData/orders.js';

export const OrderConfirmMail = defineMailTemplate(EmailType.ORDER_CONFIRM, {
  displayName: EmailType.ORDER_CONFIRM,
  defaultTemplate: {
    subject: 'FaMed - Fachsprachenprüfung Medizin - Anmeldungsbestätigung',
    content: `
<p>Sehr geehrte/r {{${EmailVars.SALUTATION}}} {{${EmailVars.LASTNAME}}},</p>
<p>hiermit bestätigen wir Ihre Anmeldung für die Teilnahme an der Fachsprachprüfung Medizin (FaMed). Der von Ihnen ausgewählte Prüfungstag ist der {{${
      EmailVars.DATE
    }}}.</p>
<p>Die Prüfungsgebühr in Höhe von 490 € (inkl. Mehrwertsteuer) wurde beglichen.</p>
<p>Ihre Prüfung wird im Zeitraum von {{${EmailVars.START_TIME}}} bis {{${
      EmailVars.END_TIME
    }}} stattfinden. Die genaue Zeit werden Sie einige Tage vor der Prüfung per Mail erfahren.</p>
<p>Sie erhalten ca. eine Woche vor Ihrem Prüfungstermin eine Erinnerungs-Email von <a>${config.get(
      'TECHNICALSUPPORTMAIL',
    )}</a> mit einer kurzen Zusammenfassung des Ablaufs am Prüfungstag.</p>
<p>Falls in der Zwischenzeit Fragen aufkommen sollten, wenden Sie sich bitte an <a>${config.get(
      'TECHNICALSUPPORTMAIL',
    )}</a>.</p>
<p>Wir wünschen Ihnen alles Gute bei der Vorbereitung auf die Fachsprachprüfung Medizin.</p>
<p>Ihr FaMed-Team</p>
`,
    footer: '',
  },
  variables: [
    EmailVars.SALUTATION,
    EmailVars.LASTNAME,
    EmailVars.DATE,
    EmailVars.START_TIME,
    EmailVars.END_TIME,
    EmailVars.EXAMDAY_TYPE,
    EmailVars.PAYMENT_PRICE,
  ],
  attachments: [EmailAttachments.EXAMDAY_ICS, EmailAttachments.INFO_PDF, EmailAttachments.INVOICE],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Prüfling<br />
      - Techsupport<br />
      - Accounting
    </p>
    <p>
      Wann:<br />
      - Wenn der Bezahlvorgang erfolgreich abgeschlossen wurde<br />
      - Wenn der Admin manuell eine Order erstellt<br />
      - Wenn der Admin manuell eine Rechnung neu erstellt<br />
      - Wenn der Admin eine Bestellung verschiebt für die neu erstellte Bestellung
    </p>
  `,
  getMailData: getOrderConfirmationMailData,
});

export const OrderConfirmMailAdmin = defineMailTemplate(EmailType.ORDER_CONFIRM_ADMIN, {
  displayName: EmailType.ORDER_CONFIRM_ADMIN,
  defaultTemplate: {
    subject: 'Zahlungsbestätigung - Admin',
    content: `Nutzer: {{${EmailVars.FIRSTNAME}}}, {{${EmailVars.LASTNAME}}}<p>
Tag: {{${EmailVars.DATE}}}, {{${EmailVars.START_TIME}}} - {{${EmailVars.END_TIME}}}<br>
Zahlungsmethode: {{${EmailVars.PAYMENT_TYPE}}}<br>
Referenznummer: {{${EmailVars.PAYMENT_REFERENCE_ID}}}<br></p>`,
    footer: '',
  },
  variables: [
    EmailVars.FIRSTNAME,
    EmailVars.LASTNAME,
    EmailVars.SALUTATION,
    EmailVars.DATE,
    EmailVars.START_TIME,
    EmailVars.END_TIME,
    EmailVars.PAYMENT_TYPE,
    EmailVars.PAYMENT_REFERENCE_ID,
    EmailVars.PAYMENT_PRICE,
  ],
  attachments: [EmailAttachments.EXAMDAY_ICS, EmailAttachments.INFO_PDF, EmailAttachments.INVOICE],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Accounting
    </p>
    <p>
      Wann:<br />
      - Wenn der Bezahlvorgang erfolgreich abgeschlossen wurde<br />
      - Wenn der Admin manuell eine Order erstellt<br />
      - Wenn der Admin manuell eine Rechnung neu erstellt<br />
      - Wenn der Admin eine Bestellung verschiebt für die neu erstellte Bestellung
    </p>
  `,
  getMailData: getOrderConfirmAdminData,
});

export const OrderRefundedMail = defineMailTemplate(EmailType.ORDER_REFUNDED, {
  displayName: EmailType.ORDER_REFUNDED,
  defaultTemplate: {
    subject: 'Rückerstattung - FaMed - Fachsprachenprüfung Medizin',
    content: `
<p>Sehr geehrte/r {{${EmailVars.SALUTATION}}} {{${EmailVars.LASTNAME}}},</p
<p>hiermit informieren wir Sie, dass Ihre Zahlung für die Teilnahme an der Fachsprachprüfung Medizin (FaMed) am {{${
      EmailVars.DATE
    }}} rückerstattet wurde, da der Termin bereits ausgebucht ist.</p
<p>Für diesen Fehler bitten wir um Verzeihung. Bei Nachfragen melden Sie sich bitte bei unserem Team unter <a>${config.get(
      'TECHNICALSUPPORTMAIL',
    )}</a>.</p
<p>Viele Grüße, Ihr FaMed-Team</p>
`,
    footer: '',
  },
  variables: [
    EmailVars.SALUTATION,
    EmailVars.FIRSTNAME,
    EmailVars.LASTNAME,
    EmailVars.DATE,
    EmailVars.START_TIME,
    EmailVars.END_TIME,
    EmailVars.PAYMENT_TYPE,
    EmailVars.PAYMENT_REFERENCE_ID,
    EmailVars.REFUND_AMOUNT,
    EmailVars.PAYMENT_PRICE,
  ],
  attachments: [EmailAttachments.INFO_PDF],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Prüfling<br />
      - Techsupport<br />
      - Accounting
    </p>
    <p>
      Wann:<br />
      - Wenn die Zahlung erfolgreich erstattet wurde
    </p>
  `,
  getMailData: async (
    order: {
      _id: string;
      scope?: Maybe<string>;
      invoiceNumber?: string;
      paymentType: string;
      processorInfo: {
        paypalPayID?: Maybe<string>;
        stripePaymentIntent?: Maybe<string>;
        stripeRefundID?: Maybe<string>;
      };
    },
    examDay: {
      _id: string;
      start: Date;
      end: Date;
      ratingType: Maybe<ExamDayRatingType>;
      examType: Maybe<ExamType>;
      prices: { unitAmount: number }[];
    },
    user: { ecommerce?: Maybe<{ firstName: string; lastName: string }>; username: string },
  ) => {
    const { baseData, scope } = await getOrderRefundBaseData(order, examDay, user);

    return {
      data: baseData,
      attachments: {
        [EmailAttachments.INFO_PDF]: ILSFile.getDownloadLink('info_famed') + '/info_famed.pdf',
      },
      scope,
    };
  },
});

export const OrderRefundedNotificationMail = defineMailTemplate(EmailType.NOTIFY_ORDER_REFUNDED, {
  displayName: EmailType.NOTIFY_ORDER_REFUNDED,
  defaultTemplate: {
    subject: 'Benachrichtigung_Zahlungserstattung',
    content: `Nutzer: {{${EmailVars.FIRSTNAME}}}, {{${EmailVars.LASTNAME}}}<p>
Tag: {{${EmailVars.DATE}}}, {{${EmailVars.START_TIME}}} - {{${EmailVars.END_TIME}}}<br>
Zahlungsmethode: {{${EmailVars.PAYMENT_TYPE}}}<br>
Preis: {{${EmailVars.PAYMENT_PRICE}}}<br>
Referenznummer: {{${EmailVars.PAYMENT_REFERENCE_ID}}}<br>
Rückerstattungsbetrag: {{${EmailVars.REFUND_AMOUNT}}}<br></p>`,
    footer: '',
  },
  variables: [
    EmailVars.FIRSTNAME,
    EmailVars.LASTNAME,
    EmailVars.DATE,
    EmailVars.START_TIME,
    EmailVars.END_TIME,
    EmailVars.PAYMENT_TYPE,
    EmailVars.PAYMENT_REFERENCE_ID,
    EmailVars.REFUND_AMOUNT,
    EmailVars.PAYMENT_PRICE,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Accounting
    </p>
    <p>
      Wann:<br />
      - Wenn die Zahlung erfolgreich erstattet wurde
    </p>
  `,
  getMailData: getOrderRefundedNotificationMailData,
});
